import React, { useRef, useEffect } from 'react';
import Box from '@mui/material/Box';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import IconButton from '@grnhse/seedling/lib/birch/components/buttons/icon_buttons';
import EditIcon from '@grnhse/seedling/lib/birch/icons/edit';
import DeleteIcon from '@grnhse/seedling/lib/birch/icons/delete';
import FormsIcon from '@grnhse/seedling/lib/birch/icons/forms';
import { ExternalLink } from '@grnhse/seedling/lib/birch/typography';
import styled from 'styled-components';
import LinkIcon from '@grnhse/seedling/lib/birch/icons/link';
import { CustomIconTooltip } from '@grnhse/seedling/lib/birch/components/tooltips';
import ReactDOM from 'react-dom';
import { withLocalePrefix } from '../../../shared/utils/translation';
import Tokens from '@grnhse/seedling/lib/birch/tokens';
import { FullPalette } from '@grnhse/seedling/lib/birch/colors';

const t = withLocalePrefix('generic_booking.tinymce_editor.link_context_menu');
const MenuBox = styled(Box)`
  border: 1px solid ${FullPalette.GREY_700};
  border-radius: 5px;
  box-shadow: 0px 2px 6px 0px rgba(21, 55, 44, 0.13);
  background: ${FullPalette.WHITE};
  min-width: 350px;
  padding: ${Tokens.spacing['2']};
`;

const StyledExternalLink = styled(ExternalLink)`
  max-width: 250px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

type LinkContextMenuProps = {
  anchorRect: DOMRect | null;
  open: boolean;
  linkUrl: string;
  onEdit: () => void;
  onRemove: () => void;
  onCopy: () => void;
  onClose: () => void;
  iframeElement?: HTMLIFrameElement | null;
  container?: HTMLElement | null;
};

type LinkActionsProps = Pick<
  LinkContextMenuProps,
  'onCopy' | 'onEdit' | 'onRemove'
>;

type LinkProps = Pick<LinkContextMenuProps, 'linkUrl'>;

const Link = ({ linkUrl }: LinkProps) => {
  return (
    <Stack direction="row" spacing={1} alignItems={'center'}>
      <LinkIcon size="sm" />
      <StyledExternalLink href={linkUrl} hidePreviewIcon={true}>
        {linkUrl}
      </StyledExternalLink>
    </Stack>
  );
};

const LinkActions = ({ onCopy, onEdit, onRemove }: LinkActionsProps) => {
  return (
    <Stack direction="row" spacing={1}>
      <CustomIconTooltip label={t('copy_link')} ariaLabel={t('copy_link')}>
        <IconButton
          icon={FormsIcon}
          label={t('copy_link')}
          onClick={onCopy}
          size="sm"
        />
      </CustomIconTooltip>
      <CustomIconTooltip label={t('edit_link')} ariaLabel={t('edit_link')}>
        <IconButton
          icon={EditIcon}
          label={t('edit_link')}
          onClick={onEdit}
          size="sm"
        />
      </CustomIconTooltip>
      <CustomIconTooltip label={t('remove_link')} ariaLabel={t('remove_link')}>
        <IconButton
          icon={DeleteIcon}
          label={t('remove_link')}
          onClick={onRemove}
          size="sm"
        />
      </CustomIconTooltip>
    </Stack>
  );
};

const LinkContextMenu: React.FC<LinkContextMenuProps> = ({
  anchorRect,
  open,
  linkUrl,
  onEdit,
  onRemove,
  onCopy,
  onClose,
  iframeElement,
  container,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // When in a portal, we need a listener for clicks outside the menu to close it
    if (!open) {
      return;
    }
    function handleClick(event: MouseEvent) {
      // outside the menu portal
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    }

    // handles outside tinymce editor
    document.addEventListener('mousedown', handleClick);
    // handles inside tinymce editor
    let iframeDoc: Document | null = null;
    if (iframeElement && iframeElement.contentDocument) {
      iframeDoc = iframeElement.contentDocument;
      iframeDoc.addEventListener('mousedown', handleClick);
    }
    return () => {
      // remove both listeners on unmount
      document.removeEventListener('mousedown', handleClick);
      if (iframeDoc) {
        iframeDoc.removeEventListener('mousedown', handleClick);
      }
    };
  }, [open, onClose, iframeElement]);

  if (!anchorRect || !open) {
    return null;
  }
  const style = {
    position: 'absolute' as const,
    top: anchorRect.top + anchorRect.height + 4,
    left: anchorRect.left,
    zIndex: 1300,
  };

  const menu = (
    <MenuBox ref={menuRef} style={style}>
      <Stack direction="row" flexWrap="wrap" justifyContent="space-between">
        <Link linkUrl={linkUrl} />
        <LinkActions onCopy={onCopy} onEdit={onEdit} onRemove={onRemove} />
      </Stack>
    </MenuBox>
  );

  return ReactDOM.createPortal(menu, container || document.body);
};

export default LinkContextMenu;
